/**
 * IdleScheduler — requestIdleCallback-based task queue
 *
 * Processes one task per idle callback so the main thread stays responsive.
 * Falls back to setTimeout(fn, 4) in browsers without requestIdleCallback (Safari < 17).
 */

type IdleHandle = number

const hasRIC = typeof requestIdleCallback === 'function'

function scheduleIdle(fn: () => void): IdleHandle {
  if (hasRIC) {
    return requestIdleCallback(fn)
  }
  return setTimeout(fn, 4) as unknown as IdleHandle
}

function cancelIdle(handle: IdleHandle): void {
  if (hasRIC) {
    cancelIdleCallback(handle)
  } else {
    clearTimeout(handle)
  }
}

export class IdleScheduler {
  private queue: (() => void)[] = []
  private handle: IdleHandle | null = null
  private onComplete?: () => void

  constructor(onComplete?: () => void) {
    this.onComplete = onComplete
  }

  /** Add a task to the queue. Starts processing if not already running. */
  enqueue(task: () => void): void {
    this.queue.push(task)
    if (this.handle === null) {
      this.scheduleNext()
    }
  }

  /** Cancel all pending tasks. */
  clear(): void {
    this.queue.length = 0
    if (this.handle !== null) {
      cancelIdle(this.handle)
      this.handle = null
    }
  }

  private scheduleNext(): void {
    if (this.queue.length === 0) {
      this.handle = null
      this.onComplete?.()
      return
    }

    this.handle = scheduleIdle(() => {
      const task = this.queue.shift()
      if (task) {
        task()
      }
      this.scheduleNext()
    })
  }
}
